import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { PLANS } from '../../../analytics/plans';
import { selectPlan } from '../../../store/plans';
import { selectHasConsent, setConsentPlanId } from '../../../store/checkout';
import { getLegalConsentHtml } from '../../../utils';

const textAlign = navigator.language.startsWith('ar') ? 'right' : 'left';

export const PaymentGate = styled.div.attrs(({ $disabled }) => ({
  inert: $disabled ? '' : undefined,
}))`
  width: 100%;
  ${({ $disabled }) => $disabled && 'opacity: 0.5; pointer-events: none;'}
`;

const Wrapper = styled.div`
  width: 100%;
  margin: 24px 0;
  text-align: ${textAlign};
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: ${props => props.theme.fontWeights.bold};
  font-size: ${props => props.theme.fontSizes.font16};
  line-height: ${props => props.theme.lineHeights.lineHeight24};
  color: ${props => props.theme.colors.colorText};
  svg {
    flex-shrink: 0;
    color: ${props => props.theme.colors.colorDomain};
  }
`;

const Body = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 16px;
  cursor: pointer;
`;

const Checkbox = styled.input`
  appearance: none;
  -webkit-appearance: none;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  margin: 2px 0 0;
  border: 1.5px solid ${props => props.theme.colors.colorInactivePlanBorder};
  border-radius: 6px;
  background: ${props => props.theme.colors.colorWhite} center / 12px no-repeat;
  cursor: pointer;
  &:checked {
    border-color: ${props => props.theme.colors.colorPrimary};
    background-color: ${props => props.theme.colors.colorPrimary};
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 10'%3E%3Cpath d='M1 5l3.5 3.5L11 1' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  }
  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.colorPrimary};
    outline-offset: 2px;
  }
`;

const Text = styled.span`
  display: block;
  font-weight: ${props => props.theme.fontWeights.extraLight};
  font-size: ${props => props.theme.fontSizes.font12};
  line-height: ${props => props.theme.lineHeights.lineHeight17};
  color: ${props => props.theme.colors.colorCheckoutOr};
  b {
    font-weight: ${props => props.theme.fontWeights.bold};
  }
  span {
    cursor: pointer;
    text-decoration: underline;
  }
`;

const InfoIcon = (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    aria-hidden='true'
  >
    <circle cx='12' cy='12' r='10.5' stroke='currentColor' strokeWidth='1.5' />
    <path
      d='M12 11V17'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
    <circle cx='12' cy='7.5' r='1.1' fill='currentColor' />
  </svg>
);

const TERMS_LINK = 'https://family-locator.com/terms-of-use-info/';
const LINKS = {
  terms: TERMS_LINK,
  privacy: 'https://family-locator.com/privacy-policy-info/',
  refund_policy: `${TERMS_LINK}#refund-policy`,
  subscription_policy: `${TERMS_LINK}#auto-renewal-policy`,
  'help-email': 'mailto:help@family-locator.com',
  'fl-link': 'https://family-locator.com',
};

const LegalConsent = ({ id }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const plan = useSelector(selectPlan);
  const hasConsent = useSelector(selectHasConsent);
  const planDetails = PLANS[plan];

  if (!planDetails) return null;

  const onLinkClick = e => {
    const url = LINKS[e.target.id];
    if (!url) return;
    e.preventDefault(); // links sit inside the checkbox label, don't toggle consent
    window.open(url);
  };

  return (
    <Wrapper>
      <Title>
        {InfoIcon}
        {t('legal_consent_title')}
      </Title>
      <Body>
        <Checkbox
          type='checkbox'
          id={id}
          checked={hasConsent}
          onChange={e =>
            dispatch(setConsentPlanId(e.target.checked ? plan : null))
          }
        />
        <Text
          dangerouslySetInnerHTML={{
            __html: getLegalConsentHtml(planDetails, t),
          }}
          onClick={onLinkClick}
        />
      </Body>
    </Wrapper>
  );
};

export default LegalConsent;
